import { Input } from '../../ui/input';
import type { FieldControlProps } from '../field-control';

/** `date`: `YYYY-MM-DD` string in and out. */
export function DateControl(props: FieldControlProps) {
  const { field, value, onChange, onBlur, id, disabled, invalid, describedBy } = props;
  return (
    <Input
      id={id}
      type="date"
      value={typeof value === 'string' ? value.slice(0, 10) : ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      onBlur={onBlur}
      readOnly={field.readOnly}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-required={field.required || undefined}
      className="w-auto"
    />
  );
}

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant → `datetime-local` value in the browser's zone; `''` when absent/invalid. */
export function toLocalInputValue(iso: unknown): string {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` value → UTC ISO string (what the API stores). */
export function fromLocalInputValue(local: string): string | undefined {
  if (local === '') return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** `datetime`: local date-time input; the stored value is an ISO string normalized to UTC. */
export function DateTimeControl(props: FieldControlProps) {
  const { field, value, onChange, onBlur, id, disabled, invalid, describedBy } = props;
  return (
    <Input
      id={id}
      type="datetime-local"
      value={toLocalInputValue(value)}
      onChange={(e) => onChange(fromLocalInputValue(e.target.value))}
      onBlur={onBlur}
      readOnly={field.readOnly}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-required={field.required || undefined}
      className="w-auto"
    />
  );
}
